import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { api } from "../../services/api";
import { useNotification } from "../../contexts/NotificationContext";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const SHIFTS = [
  { value: "1", label: "Shift 1 — Morning (07:00–15:30)" },
  { value: "2", label: "Shift 2 — Evening (15:00–23:30)" },
  { value: "3", label: "Shift 3 — Night (23:00–07:30)" },
];

function buildFormData(file, params) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("year", String(params.year));
  fd.append("month", String(params.month));
  fd.append("shift", String(params.shift));
  fd.append("removeStrayRows", String(params.removeStrayRows));
  fd.append("createMissingEmployees", String(params.createMissingEmployees));
  if (params.confirmPhrase) fd.append("confirmPhrase", params.confirmPhrase);
  if (params.expectedCreateCount != null) {
    fd.append("expectedCreateCount", String(params.expectedCreateCount));
  }
  return fd;
}

/** Guess year / month / shift from names like Shift-1_2026-06_dummy.xlsx */
function guessMetaFromFilename(filename = "") {
  const name = String(filename || "");
  const meta = { year: null, month: null, shift: null };

  // Put 10–12 before 1–9 so "2025-11" → month 11 (not month 1).
  const ym = name.match(/(20\d{2})[-_/](1[0-2]|0?[1-9])/);
  if (ym) {
    meta.year = Number(ym[1]);
    meta.month = Number(ym[2]);
  }

  const shiftMatch =
    name.match(/shift\s*[-_]?\s*([123])/i) ||
    name.match(/\b([123])(?:st|nd|rd)?\s*shift\b/i);
  if (shiftMatch) meta.shift = String(Number(shiftMatch[1]));

  return meta;
}

const AttendanceSeedPage = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useNotification();
  const now = new Date();

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [shift, setShift] = useState("1");
  const [file, setFile] = useState(null);
  const [fileHint, setFileHint] = useState(null);
  const [removeStrayRows, setRemoveStrayRows] = useState(true);
  const [createMissingEmployees, setCreateMissingEmployees] = useState(true);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");

  const params = { year, month, shift, removeStrayRows, createMissingEmployees };

  const filenameMismatch =
    fileHint &&
    ((fileHint.year && fileHint.year !== Number(year)) ||
      (fileHint.month && fileHint.month !== Number(month)) ||
      (fileHint.shift && fileHint.shift !== String(shift)));

  const requiredPhrase = preview?.confirmPhrase || "";
  const willCreate = preview?.willCreate ?? preview?.validPresentMarks ?? preview?.totalPresentMarks;
  const willDelete = preview?.willDeleteApprox ?? preview?.existingMonthAttendanceCount ?? 0;
  const previewOk = Boolean(preview) && preview.totalsMatch !== false && !filenameMismatch;
  const confirmOk = Boolean(requiredPhrase) && confirmPhrase.trim() === requiredPhrase;
  const canSync = previewOk && confirmOk && !loading && Boolean(file);

  // Changing target month/shift/file invalidates preview — client must preview again.
  useEffect(() => {
    setPreview(null);
    setConfirmPhrase("");
  }, [year, month, shift, removeStrayRows, createMissingEmployees]);

  const applyFileMeta = (selectedFile) => {
    setFile(selectedFile || null);
    setPreview(null);
    setResult(null);
    setConfirmPhrase("");
    if (!selectedFile) {
      setFileHint(null);
      return;
    }
    const hint = guessMetaFromFilename(selectedFile.name);
    setFileHint(hint);
    if (hint.year) setYear(hint.year);
    if (hint.month) setMonth(hint.month);
    if (hint.shift) setShift(hint.shift);
  };

  const handlePreview = async () => {
    if (!file) {
      showError("Please select an Excel file first");
      return;
    }
    if (filenameMismatch) {
      showError("Fix year/month/shift to match the filename before preview");
      return;
    }
    setLoading(true);
    setResult(null);
    setConfirmPhrase("");
    try {
      const response = await api.post(
        "/admin/attendance-seed/preview",
        buildFormData(file, params),
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }
      );
      setPreview(response.data?.data || null);
    } catch (err) {
      showError(err.response?.data?.message || "Preview failed");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!file || !preview) {
      showError("Run Preview first");
      return;
    }
    if (filenameMismatch) {
      showError("Filename year/month/shift must match the form. Sync blocked.");
      return;
    }
    if (!confirmOk) {
      showError(`Type exactly: ${requiredPhrase}`);
      return;
    }

    const monthLabel = MONTHS.find((m) => m.value === Number(month))?.label || month;
    const ok = window.confirm(
      `FINAL CONFIRM — this writes to the connected database (often LIVE).\n\n` +
        `Month: ${monthLabel} ${year}\n` +
        `Shift: ${preview.shift}\n` +
        `DELETE about ${willDelete} existing ${preview.shift} rows\n` +
        `CREATE ${willCreate} rows from the sheet\n\n` +
        `Other shifts in this month are not changed.\n\nContinue?`
    );
    if (!ok) return;

    setLoading(true);
    try {
      const response = await api.post(
        "/admin/attendance-seed/sync",
        buildFormData(file, {
          ...params,
          confirmPhrase: confirmPhrase.trim(),
          expectedCreateCount: willCreate,
        }),
        {
          headers: { "Content-Type": "multipart/form-data" },
          // Bulk seed of ~2k+ rows can exceed the default 60s axios timeout.
          timeout: 300000,
        }
      );
      setResult(response.data?.data || null);
      setPreview(null);
      setConfirmPhrase("");
      success(response.data?.message || "Attendance synced successfully");
    } catch (err) {
      const timedOut =
        err.code === "ECONNABORTED" ||
        /timeout/i.test(String(err.message || "")) ||
        err.response?.status === 504;
      showError(
        timedOut
          ? "Sync timed out on the browser, but the server may still be writing. Wait 1–2 min, then check Attendance for that month/shift. Retry if counts are wrong."
          : err.response?.data?.message || "Sync failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Monthly Attendance Seed</h1>
        <p className="text-secondary-600 mt-1">
          Super-admin only. Uploads a muster sheet into Attendance + Muster for one month/shift.
        </p>
      </div>

      <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 flex gap-3">
        <AlertTriangle className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
        <div className="text-sm text-red-950 space-y-1">
          <p className="font-semibold text-base">Destructive live operation</p>
          <p>
            Your local backend is often connected to the <strong>live MongoDB</strong>. Syncing here
            can delete and replace real client attendance for that month/shift.
          </p>
          <p>
            Safe monthly process: correct filename → Preview → verify delete/create numbers → type
            the confirm phrase → Sync → check Attendance + Muster.
          </p>
        </div>
      </div>

      <div className="card p-6 border border-secondary-200 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">Year</label>
            <input
              type="number"
              min={2020}
              max={2035}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full border border-secondary-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full border border-secondary-300 rounded-lg px-3 py-2"
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">Shift</label>
            <select
              value={shift}
              onChange={(e) => setShift(e.target.value)}
              className="w-full border border-secondary-300 rounded-lg px-3 py-2"
            >
              {SHIFTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Excel file (.xlsx)</label>
          <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-secondary-300 rounded-lg cursor-pointer hover:bg-secondary-50 transition-colors">
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => applyFileMeta(e.target.files?.[0] || null)}
            />
            {file ? (
              <>
                <FileSpreadsheet className="h-8 w-8 text-green-600 mb-2" />
                <span className="text-sm font-medium text-secondary-800">{file.name}</span>
                <span className="text-xs text-secondary-500 mt-1">Click to change file</span>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-secondary-400 mb-2" />
                <span className="text-sm text-secondary-600">Click to upload muster sheet</span>
              </>
            )}
          </label>
          {fileHint && (fileHint.year || fileHint.month || fileHint.shift) && (
            <p className="text-xs text-secondary-600 mt-2">
              Detected from filename:{" "}
              {[
                fileHint.month
                  ? MONTHS.find((m) => m.value === fileHint.month)?.label
                  : null,
                fileHint.year,
                fileHint.shift ? `Shift ${fileHint.shift}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              {" — applied to the form above."}
            </p>
          )}
          {filenameMismatch && (
            <p className="text-sm text-red-700 mt-2 font-medium">
              Form year/month/shift does not match the filename. Sync is blocked until you fix it.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-start gap-2 text-sm text-secondary-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={removeStrayRows}
              onChange={(e) => setRemoveStrayRows(e.target.checked)}
            />
            <span>
              <strong>Full replace this shift for the month</strong> — deletes ALL existing{" "}
              {SHIFTS.find((s) => s.value === shift)?.label || "shift"} rows in this month, then
              inserts only the sheet. Other shifts are kept. Leave ON for client monthly muster sync.
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm text-secondary-700">
            <input
              type="checkbox"
              checked={createMissingEmployees}
              onChange={(e) => setCreateMissingEmployees(e.target.checked)}
            />
            Create missing employees from sheet (password: Panther@123)
          </label>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="button"
            onClick={handlePreview}
            disabled={loading || !file || filenameMismatch}
            className="btn-secondary flex items-center"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            1. Preview
          </button>
          <button
            type="button"
            onClick={handleSync}
            disabled={!canSync}
            className="btn-primary flex items-center"
            title={!previewOk ? "Preview first" : !confirmOk ? "Type confirm phrase" : "Sync"}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            2. Sync attendance
          </button>
        </div>
        {!preview && (
          <p className="text-xs text-secondary-500">Sync stays locked until Preview succeeds.</p>
        )}
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Client monthly checklist</p>
          <ul className="mt-1 list-disc list-inside space-y-1 text-amber-800">
            <li>
              Filename: <code>Shift-1_2025-10_RF.xlsx</code> → October 2025, morning only.
            </li>
            <li>Footer TOTAL must equal counted P marks or sync is blocked.</li>
            <li>Only one shift per sync. Run Shift 2 / Shift 3 separately.</li>
            <li>After sync, verify Attendance + Muster both show the sheet TOTAL.</li>
          </ul>
        </div>
      </div>

      {preview && (
        <div className="card p-6 border border-blue-200 bg-blue-50/50 space-y-4">
          <h2 className="font-semibold text-secondary-900 flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
            Preview — {preview.sheetName}
            {preview.liveDatabase ? (
              <span className="ml-2 text-xs font-bold uppercase tracking-wide text-red-700 bg-red-100 px-2 py-0.5 rounded">
                Live DB
              </span>
            ) : null}
          </h2>

          <div className="rounded-lg border border-red-200 bg-white p-4 space-y-2">
            <p className="text-sm font-semibold text-red-800">Impact if you sync now</p>
            <p className="text-sm text-secondary-800">
              DELETE about <strong>{willDelete}</strong> existing <strong>{preview.shift}</strong>{" "}
              rows for{" "}
              <strong>
                {MONTHS.find((m) => m.value === Number(preview.month))?.label || preview.month}{" "}
                {preview.year}
              </strong>
              , then CREATE <strong>{willCreate}</strong> rows from this sheet.
            </p>
            <label className="block text-sm font-medium text-secondary-800 pt-2">
              Type this exact phrase to unlock Sync:
            </label>
            <code className="block text-sm bg-secondary-100 border border-secondary-200 rounded px-3 py-2 select-all">
              {requiredPhrase}
            </code>
            <input
              type="text"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              placeholder={requiredPhrase}
              className="w-full border border-secondary-300 rounded-lg px-3 py-2 font-mono text-sm mt-1"
              autoComplete="off"
              spellCheck={false}
            />
            {confirmPhrase && !confirmOk && (
              <p className="text-xs text-red-600">Phrase does not match yet.</p>
            )}
            {confirmOk && (
              <p className="text-xs text-green-700 font-medium">Confirm phrase OK — Sync unlocked.</p>
            )}
          </div>

          {preview.totalsMatch === true && (
            <p className="text-sm text-green-700 font-medium">
              Totals match: counted {preview.totalPresentMarks} P = footer TOTAL {preview.sheetGrandTotal}
            </p>
          )}
          {preview.totalsMatch === false && (
            <p className="text-sm text-red-700 font-medium">
              Totals do not match: counted {preview.totalPresentMarks} P vs footer TOTAL{" "}
              {preview.sheetGrandTotal}. Sync is blocked until this matches.
            </p>
          )}
          {preview.totalsMatch == null && (
            <p className="text-sm text-amber-700">
              No footer TOTAL found — counted {preview.totalPresentMarks} P marks from employee rows.
            </p>
          )}
          {preview.invalidDayMarks > 0 && (
            <p className="text-sm text-amber-800 font-medium">
              {preview.invalidDayMarks} P mark(s) fall on days that do not exist in{" "}
              {MONTHS.find((m) => m.value === Number(preview.month))?.label || preview.month}{" "}
              {preview.year} (month has {preview.daysInSelectedMonth} days). Those will be skipped.
              Will create {preview.validPresentMarks} rows.
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Stat label="Employees in sheet" value={preview.employeeCount} />
            <Stat label="Total P marks" value={preview.totalPresentMarks} />
            <Stat label="Will create" value={willCreate} />
            <Stat label="Will delete (approx)" value={willDelete} />
            <Stat label="Footer TOTAL" value={preview.sheetGrandTotal ?? "—"} />
            <Stat
              label="Totals match"
              value={
                preview.totalsMatch === true
                  ? "Yes"
                  : preview.totalsMatch === false
                    ? "No"
                    : "N/A"
              }
            />
            <Stat label="Missing in DB" value={preview.missingEmployeeCount} />
            <Stat label="Shift" value={preview.shift} />
          </div>
          {preview.sampleEmployees?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-secondary-200 rounded-lg overflow-hidden bg-white">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">P days</th>
                    <th className="px-3 py-2 text-left">Location</th>
                    <th className="px-3 py-2 text-left">In DB</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sampleEmployees.map((e) => (
                    <tr key={e.code} className="border-t border-secondary-100">
                      <td className="px-3 py-2 font-mono">{e.code}</td>
                      <td className="px-3 py-2">{e.name || "—"}</td>
                      <td className="px-3 py-2">{e.presentCount}</td>
                      <td className="px-3 py-2">{e.loc || "—"}</td>
                      <td className="px-3 py-2">{e.existsInDb ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-secondary-500 mt-2">Showing first 10 employees</p>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="card p-6 border border-green-200 bg-green-50/50 space-y-3">
          <h2 className="font-semibold text-secondary-900 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Sync complete
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Stat label="Processed" value={result.employeesProcessed} />
            <Stat label="Created employees" value={result.employeesCreated} />
            <Stat label="Skipped" value={result.employeesSkipped} />
            <Stat label="Rows deleted" value={result.attendanceDeleted} />
            <Stat label="Rows created" value={result.attendanceCreated} />
            <Stat label="Stray rows removed" value={result.strayRowsRemoved} />
            <Stat label="Invalid day P skipped" value={result.invalidDayMarksSkipped ?? 0} />
            <Stat
              label="Target month"
              value={`${MONTHS.find((m) => m.value === Number(result.month))?.label || result.month} ${result.year}`}
            />
            <Stat label="Shift" value={result.shift} />
          </div>
          <p className="text-sm text-secondary-700">
            Verify on Attendance Management and Muster Report for{" "}
            <strong>
              {MONTHS.find((m) => m.value === Number(result.month))?.label || result.month}{" "}
              {result.year}
            </strong>
            , shift <strong>{result.shift}</strong>. Both should match rows created (
            <strong>{result.attendanceCreated}</strong>).
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2 text-sm"
              onClick={() =>
                navigate(
                  `/admin/attendance?year=${result.year}&month=${result.month}&shift=${encodeURIComponent(result.shift)}`
                )
              }
            >
              <ExternalLink className="h-4 w-4" />
              Open Attendance Management
            </button>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2 text-sm"
              onClick={() =>
                navigate(
                  `/admin/reports?year=${result.year}&month=${result.month}&shift=${encodeURIComponent(result.shift)}`
                )
              }
            >
              Open Muster Report
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-lg border border-secondary-200 px-3 py-2">
      <p className="text-secondary-500 text-xs">{label}</p>
      <p className="font-semibold text-secondary-900">{value}</p>
    </div>
  );
}

export default AttendanceSeedPage;
